import { useEffect, useMemo, useRef, useState } from "react";
import { ArrowDownCircle, ArrowLeft, ArrowRight, ArrowUpCircle, Camera, Check, ChevronLeft, ChevronRight, Loader2, Lock, MessageCircle, Pencil, Upload, X } from "lucide-react";
import {
  BILLS, CUR_KEY, CUR_M, CUR_Y, MONTHS, MONTHS_SHORT, now, rentDayOf, billsDayOf,
  activeIn, billsUtility, consumptionBillsText, currentTenancy,
  effectiveRent, effectiveVaad, emptyBill, entryFor, itemDue, itemPaid, mKey, messageMode, monthLabel, nis,
  pad, payMethodOf, prevMonth, readingFloor, readingsOf, readingSanity, recordMeterReading, rateFor, rentDebtReminderText, rentPaidOf, roundBillAmount, rowKey,
  tenanciesOf, tenancyEnd, tenancyForMonth, TODAY, UNIT, upcomingTenancies, heDate, vaadDebtReminderText, waLink,
} from "@rental/shared";
import { C, inputStyle } from "./theme.js";
import MethodPick from "./MethodPick.jsx";
import { ApiError, ai, uploadImage } from "./lib/api.js";
import { compressForUpload } from "./lib/images.js";
import { FilePick } from "./ui.jsx";

/* ============================================================================
   לוחות הגבייה בסקירה — שלושה לוחות על אותה רשת של נכסים × חודשים:

     שכר דירה   — ירוק.   לחיצה מסמנת שהתקבל (כמו קודם).
     ועד בית    — כחול.   לחיצה מסמנת שהתקבל; הסכום הוא מה שנרשם לחודש,
                          ואם עוד לא נרשם — ועד הבית של התקופה.
     צריכה      — חום.    חשמל, מים, גז (וביוב ישן). לחיצה פותחת חלון עם
                          פירוט, עיגול וסימון — כי זה סכום מורכב מכמה סעיפים
                          וכמעט תמיד רוצים לעגל אותו לפני שמבקשים מהדייר.

   כל תא מציג את הסכום שהדייר צריך לשלם באותו חודש, ובתחתית כל לוח שורת
   סה"כ: מה נגבה מול מה צפוי. לוח בלי סכומים היה רק "וי" — ובעל הנכס
   שאל כל פעם "כמה זה".
   ============================================================================ */

const CONSUMPTION = ["elec", "water", "gas", "sewage"];
const BILL_LABEL = Object.fromEntries(BILLS.map((b) => [b.k, b.label]));

export const VAAD_COLOR = "#3D6B99";
export const CONS_COLOR = "#A85C2A";

const fmt = (n) => Math.round(n || 0).toLocaleString("he-IL");

/* סכום מדויק לתצוגת החלון: nis מעגל לתצוגה, ולכן סה"כ של 123.47 הוצג
   ₪123 עוד לפני העיגול — ו"לשקל" נראה ככפתור שאינו עושה דבר. */
const nisX = (n) => "₪" + (Math.round((n || 0) * 100) / 100).toLocaleString("he-IL", { maximumFractionDigits: 2 });
const numX = (n) => Number(n || 0).toLocaleString("he-IL", { maximumFractionDigits: 2 });

/* ============ שני פריסות לאותה רשת ============
   במחשב: 12 חודשים בשורה, תא צר לכל חודש — סקירה של שנה במבט אחד.
   בטלפון: חודש אחד בכל פעם, עם חיצים — 12 תאים ברוחב 52px לא נכנסים
   למסך, והגלילה הצידה הסתירה את שם הנכס ואת החודש הנוכחי. אותם נתונים
   ואותן פעולות; רק מספר העמודות ורוחבן משתנים. */
const DESKTOP = { cellW: 52, cellH: 40, labelW: 152 };
const MOBILE = { cellW: 104, cellH: 46, labelW: 0 };   // labelW 0 = תופס את כל הרוחב שנשאר

function useNarrow() {
  const q = "(max-width: 639px)";
  const [narrow, setNarrow] = useState(() => typeof window !== "undefined" && window.matchMedia(q).matches);
  useEffect(() => {
    const mq = window.matchMedia(q);
    const on = (e) => setNarrow(e.matches);
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, []);
  return narrow;
}

/* תא אחד ברשת. אותו רכיב לשלושת הלוחות — רק הצבע והפעולה משתנים.
   onEdit (רק שכר דירה / ועד בית): עיפרון קטן בפינת התא, לסימון עם סכום
   שונה מהצפוי — למשל תשלום היסטורי שנגבה לפני שהדייר נרשם במערכת.
   הלחיצה הרגילה על גוף התא נשארת בדיוק כמו היום — מהירה, סכום מלא. */
function Cell({ amount, paid, partial, past, future, color, onClick, onEdit, title, disabled, open = false, dim = DESKTOP, action = null }) {
  const empty = !(amount > 0);
  /* open — תא ריק שאפשר ללחוץ עליו כדי להזין (חשבון צריכה שעוד לא חושב). */
  const inert = disabled || (empty && !open);
  const bg = empty ? (open ? C.card : "#F4F7F6") : paid ? color : past ? "#FFFBFA" : C.card;
  const border = empty ? C.line : paid ? color : past ? "#EBC7BF" : C.line;
  const text = paid ? "#fff" : past && !empty ? C.late : C.ink;
  const big = dim.cellW > 80;
  const editable = !!onEdit && !inert && !paid;
  return (
    <div role="button" tabIndex={inert ? -1 : 0} aria-disabled={inert} title={title}
      onClick={inert ? undefined : onClick}
      onKeyDown={inert ? undefined : (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onClick(); } }}
      className="relative flex flex-col items-center justify-center shrink-0 leading-none"
      style={{
        width: dim.cellW, height: dim.cellH, background: bg, color: text, borderRadius: 10,
        border: `1px ${future || empty ? "dashed" : "solid"} ${border}`,
        opacity: future ? 0.5 : 1,
        cursor: inert ? "default" : "pointer",
      }}>
      {empty ? (big && open ? <span style={{ fontSize: 11, color: C.muted }}>להזנה</span> : null) : (
        <>
          <span className="flex items-center gap-0.5" style={{ fontSize: big ? 14 : 10, fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>
            {paid && <Check size={big ? 13 : 10} />}{fmt(amount)}
          </span>
          {partial > 0 && !paid ? (
            <span style={{ fontSize: big ? 10 : 8, color: C.muted, marginTop: 2 }}>נותרו {fmt(partial)}</span>
          ) : !paid && big && action && !inert ? (
            /* מה הלחיצה עושה — כתוב בתוך התא. סכום בלי הסבר נראה כמו
               תצוגה בלבד, ואיש לא מנחש שלחיצה מסמנת תשלום. */
            <span style={{ fontSize: 9, color: past ? C.late : C.muted, marginTop: 2 }}>{action}</span>
          ) : null}
          {!paid && !partial && past && !big && <span className="rounded-full" style={{ width: 5, height: 5, background: C.late, marginTop: 2 }} />}
        </>
      )}
      {editable && (
        <button type="button" onClick={(e) => { e.stopPropagation(); onEdit(); }}
          aria-label="הזנת סכום שונה מהצפוי" title="הזנת סכום שונה מהצפוי"
          className="absolute flex items-center justify-center"
          style={{
            top: -6, insetInlineStart: -6, width: 18, height: 18, borderRadius: 999, padding: 0,
            background: C.card, border: `1px solid ${C.line}`, color: C.muted,
          }}>
          <Pencil size={11} />
        </button>
      )}
    </div>
  );
}

function Board({ title, hint, color, rows, totals, empty, filterSlot, children, dim = DESKTOP }) {
  const labelStyle = dim.labelW ? { width: dim.labelW } : { flex: 1, minWidth: 0 };
  return (
    <div className="mt-4 first:mt-0">
      <div className="flex items-center gap-2 mb-2 justify-between">
        <div className="flex items-center gap-2 min-w-0">
          <span className="rounded-full shrink-0" style={{ width: 10, height: 10, background: color }} />
          <h3 className="text-sm shrink-0" style={{ fontWeight: 600 }}>{title}</h3>
          {/* הרמז מוצג גם בטלפון — דווקא שם, עם תא אחד גדול, לא ברור
              שהלחיצה מסמנת תשלום. */}
          {hint && <span className="text-[11px] min-w-0 truncate" style={{ color: C.muted }}>· {hint}</span>}
        </div>
        {filterSlot && <div className="shrink-0">{filterSlot}</div>}
      </div>
      {rows.length ? rows : <div className="text-[12px] py-2" style={{ color: C.muted }}>{empty}</div>}
      {totals && rows.length > 0 && (
        <div className="flex items-center gap-1 py-1 mt-1 border-t" style={{ borderColor: C.line }}>
          <div className="pl-3 shrink-0 text-[11px]" style={{ ...labelStyle, color: C.muted }}>
            סה״כ <span style={{ color }}>נגבה</span> / צפוי
          </div>
          {totals.map((t, i) => (
            <div key={i} className="flex flex-col items-center justify-center shrink-0 leading-none" style={{ width: dim.cellW, height: 34 }}>
              <span style={{ fontSize: dim.cellW > 80 ? 13 : 10, fontWeight: 700, color, fontVariantNumeric: "tabular-nums" }}>{t.expected ? fmt(t.paid) : ""}</span>
              <span style={{ fontSize: dim.cellW > 80 ? 11 : 9, color: C.muted, marginTop: 2, fontVariantNumeric: "tabular-nums" }}>{t.expected ? fmt(t.expected) : "—"}</span>
            </div>
          ))}
        </div>
      )}
      {children}
    </div>
  );
}

const STATUS_OPTIONS = [
  { k: "active", label: "פעיל" },
  { k: "departed", label: "עזב" },
  { k: "cancelled", label: "בוטל" },
];
const DEFAULT_STATUS_FILTER = { active: true, departed: true, cancelled: false };

/* קומבו רב-בחירה: אילו תקופות מוצגות בלוח הזה — פעיל / עזב / בוטל.
   כל לוח (שכר דירה / ועד / צריכה) מחזיק את הבחירה שלו בנפרד; שורת
   "פנויה" (בלי תקופה) לא מושפעת — היא לא סטטוס של תקופה. */
function StatusFilter({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onEsc = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onEsc);
    return () => { document.removeEventListener("mousedown", onDoc); document.removeEventListener("keydown", onEsc); };
  }, [open]);

  const picked = STATUS_OPTIONS.filter((o) => value[o.k]);
  const summary = picked.length === STATUS_OPTIONS.length ? "הכול" : picked.map((o) => o.label).join(", ") || "כלום";

  return (
    <div className="relative" ref={ref}>
      <button type="button" onClick={() => setOpen(!open)} aria-expanded={open} aria-haspopup="menu"
        className="rounded-lg px-2 py-1 text-[11px] flex items-center gap-1 whitespace-nowrap"
        style={{ border: `1px solid ${open ? C.teal : C.line}`, color: C.muted }}>
        {summary} ⌄
      </button>
      {open && (
        <div role="menu" className="absolute z-50 rounded-lg border py-1 shadow-lg"
          style={{ background: C.card, borderColor: C.line, insetInlineEnd: 0, top: "calc(100% + 4px)", minWidth: 100 }}>
          {STATUS_OPTIONS.map((o) => (
            <label key={o.k} className="flex items-center gap-2 px-3 py-1.5 text-xs cursor-pointer">
              <input type="checkbox" checked={!!value[o.k]}
                onChange={(e) => onChange({ ...value, [o.k]: e.target.checked })} />
              {o.label}
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

/* סכום הצריכה בחודש — חשמל, מים, גז, ביוב. */
const consumptionOf = (b) => {
  const items = CONSUMPTION.map((k) => b?.[k]).filter(Boolean);
  const amount = items.reduce((t, it) => t + Number(it.amount || 0), 0);
  const paid = items.reduce((t, it) => t + itemPaid(it), 0);
  // נטו — עודף על סעיף אחד מקזז סעיף אחר באותו חודש (אותו כלל כמו billsDue).
  const due = Math.max(0, items.reduce((t, it) => t + itemDue(it), 0));
  return { amount, paid, due, isPaid: amount > 0 && due <= 0 };
};

/* ============================================================================
   שני אייקונים קומפקטיים (מצלמה + העלאה) ליד שדה הסכום — בלי מסך
   MeterBlock המלא. הזיהוי ממלא את שדה הסכום עצמו במספר הקריאה (לא
   בסכום ה-₪) — תיקון, אם צריך, נעשה ישירות בשדה. השמירה בפועל קורית
   רק בלחיצה על "אישור קריאות" המשותף (ב-ConsumptionModal), לא כאן —
   קריאה שגויה שנשמרת לבד = חשבון שגוי לדייר.
   ============================================================================ */
function MeterScanButtons({ apt, meter, onScanned, disabled }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const scan = async (file) => {
    if (!file) return;
    setBusy(true); setErr("");
    try {
      const small = await compressForUpload(file);
      let photoId = null;
      try { photoId = await uploadImage(small, { scope: "owner", kind: "meter", apartmentId: apt.id }); }
      catch { photoId = null; }
      const out = await ai.readMeter(meter, small, { scope: "owner" });
      if (out.reading == null) setErr(out.note || "לא הצלחתי לקרוא את המונה מהתמונה.");
      else onScanned(meter, { value: out.reading, photoId });
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : "קריאת התמונה נכשלה.");
    }
    setBusy(false);
  };

  return (
    <div className="flex items-center gap-1 shrink-0">
      <FilePick capture title="צילום המונה" disabled={busy || disabled} onPick={scan}
        className="rounded-lg p-1.5 flex items-center" style={{ border: `1px solid ${C.line}`, color: C.teal }}>
        {busy ? <Loader2 size={13} className="animate-spin" /> : <Camera size={13} />}
      </FilePick>
      <FilePick title="בחירת תמונה מהגלריה" disabled={busy || disabled} onPick={scan}
        className="rounded-lg p-1.5 flex items-center" style={{ border: `1px solid ${C.line}`, color: C.muted }}>
        <Upload size={13} />
      </FilePick>
      {err && <span className="text-[10px]" style={{ color: C.late }}>{err}</span>}
    </div>
  );
}

/* ============================================================================
   חלון חשבון הצריכה של חודש אחד: פירוט לפי סעיף, עיגול וסימון.

   העיגול משנה את הסעיף הגדול ביותר כך שהסה"כ ייצא עגול — הסעיפים האחרים
   נשארים כפי שחושבו מהמונים, ומה שהדייר רואה זה סכום אחד נוח.

   שני כפתורים בלבד, חץ בעיגול למעלה או למטה (24.8.2026 — קודם היו בורר
   כיוון וארבעה כפתורי צעד): הלחיצה הראשונה מוחקת את האגורות, וכל לחיצה
   נוספת מזיזה שקל שלם בכיוון החץ — לחיצה = שקל, עד לסכום שרוצים.
   הכיוון בידי בעל הנכס: עיגול למעלה הוא חיוב על צריכה שלא הייתה, ומי
   שבוחר בו — שיבחר במפורש.
   ============================================================================ */
function ConsumptionModal({ apt, tenancy, month, data, setData, run, onClose }) {
  const rawBill = data.bills[rowKey(apt.id, month)] || emptyBill();
  /* התקופה "הראשית" של החודש כותבת/קוראת מהשדות הרגילים כמו תמיד;
     תקופה משנית (למשל דייר שנכנס באמצע חודש שבו כבר יש חשבון ליוצא)
     מציגה את entries שלה בלבד, ומשמרת את שאר השדות (elecRead/photos
     וכו') כפי שהם — ראו docs/superpowers/specs/2026-08-28-collection-board-tenancy-rows-design.md. */
  const primary = tenancyForMonth(data, apt.id, month)?.id === tenancy.id;
  const bill = {
    ...rawBill,
    ...Object.fromEntries(CONSUMPTION.map((k) => [k, entryFor(rawBill[k], tenancy.id, primary)])),
  };
  const [amounts, setAmounts] = useState(() =>
    Object.fromEntries(CONSUMPTION.map((k) => [k, Number(bill[k]?.amount || 0)])));

  /* צילום מונה (MeterPhotoIcons, ליד כל שדה) כותב ישירות ל-data.bills
     דרך recordMeterReading — לא דרך amounts המקומי, ובלי לחכות ל"שמירת
     הסכומים". הסנכרון הזה מעדכן את השדה בתצוגה כשזה קורה, בלי לדרוס
     עריכה ידנית של המשתמש שלא נובעת מקריאה חדשה.
     סעיף עם קריאה ממתינה (pendingReadings) מודר מהסנכרון הזה: אחרת
     אישור קריאה אחת (למשל חשמל) היה מפעיל את ה-effect ודורס בשקט את
     הקריאה שהמשתמש עדיין מקליד במים — למרות שהיא לא נשמרה ולא אושרה. */
  useEffect(() => {
    setAmounts((prev) => ({
      ...prev,
      ...(pendingReadings.elec ? {} : { elec: Number(bill.elec?.amount || 0) }),
      ...(pendingReadings.water ? {} : { water: Number(bill.water?.amount || 0) }),
      ...(pendingReadings.gas ? {} : { gas: Number(bill.gas?.amount || 0) }),
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bill.elec?.amount, bill.water?.amount, bill.gas?.amount]);

  /* שדה הקריאה נפרד לגמרי משדה הסכום (₪) — עד 1.9.2026 שדה אחד שימש גם
     לקריאה וגם לסכום, לפי מצב "ממתין", וזה בלבל: אותו מספר-מונה נראה
     כאילו הוא הסכום לתשלום. readingDrafts הוא הטיוטה שמוצגת בשדה הקריאה
     כשיש קריאה ממתינה (pendingReadings) — עד אז השדה מציג את הקריאה
     האחרונה שנשמרה (דרך readInfo). jumpWarned מסמן קפיצה חריגה שכבר
     הוצגה פעם אחת — לחיצה נוספת על "אישור" שומרת בכל זאת. */
  const [pendingReadings, setPendingReadings] = useState({}); // { [meter]: { photoId, jumpWarned } }
  const [readingDrafts, setReadingDrafts] = useState({}); // { [meter]: value }
  const [readingErrors, setReadingErrors] = useState({}); // { [meter]: message }

  const onScanned = (meter, { value, photoId }) => {
    setReadingDrafts((prev) => ({ ...prev, [meter]: value }));
    setPendingReadings((prev) => ({ ...prev, [meter]: { photoId, jumpWarned: false } }));
    setReadingErrors((prev) => ({ ...prev, [meter]: null }));
  };

  /* הקלדה ישירה לשדה הקריאה: התו הראשון נכנס למצב "ממתין" (כמו קריאה
     מזוהה מתמונה, רק בלי photoId), כדי שאישור/נוסחה חיה יעבדו זהה. */
  const onReadingChange = (meter, raw) => {
    setReadingDrafts((prev) => ({ ...prev, [meter]: raw === "" ? "" : Number(raw) || 0 }));
    setPendingReadings((prev) => (prev[meter] ? prev : { ...prev, [meter]: { photoId: null, jumpWarned: false } }));
    setReadingErrors((prev) => ({ ...prev, [meter]: null }));
  };

  /* אישור לפי סעיף בודד — כפתור נפרד ליד כל קריאה, לא כפתור אחד משותף:
     אישור של חשמל לא נוגע במים שעדיין ממתינים, ואין "לא ברור אם זה שומר
     את שניהם ביחד". */
  const confirmOne = (meter) => {
    const pend = pendingReadings[meter];
    if (!pend) return;
    const isCur = month === CUR_KEY;
    const monthEnd = `${month}-${pad(new Date(Number(month.slice(0, 4)), Number(month.slice(5, 7)), 0).getDate())}`;
    const date = isCur ? TODAY : monthEnd;
    const value = readingDrafts[meter];
    const check = readingSanity(data, apt.id, meter, value, date);
    if (check.code === "not_a_number") { setReadingErrors((p) => ({ ...p, [meter]: "צריך להזין מספר" })); return; }
    if (check.code === "future_date") { setReadingErrors((p) => ({ ...p, [meter]: "התאריך עתידי" })); return; }
    if (check.code === "below_previous") { setReadingErrors((p) => ({ ...p, [meter]: `הקריאה נמוכה מהקודמת (${check.previous}) — בדקו את המספר` })); return; }
    if (check.code === "jump" && !pend.jumpWarned) {
      setReadingErrors((p) => ({ ...p, [meter]: `${Math.round(check.diff)} ${UNIT[meter]} ב-${check.days} ימים — הרבה מעל הרגיל. לחצו שוב לאישור.` }));
      setPendingReadings((p) => ({ ...p, [meter]: { ...p[meter], jumpWarned: true } }));
      return;
    }
    recordMeterReading(setData, apt.id, meter, value, date, pend.photoId);
    /* ברגע שהסעיף הופך ללא-ממתין, billAmount קורא את amounts[meter] כ-₪
       ישירות. אם recordMeterReading לא עדכן את הסכום (הפרש 0 או שלילי —
       למשל קריאה שנייה על אותו יום/ערך) השדה נשאר תקוע עם ערך ישן, וה-
       effect שמסנכרן מ-bill[...].amount לא רץ שוב כי הסכום בפועל לא
       השתנה. תיקון מיידי: לחזור לסכום השמור הנוכחי כברירת מחדל — אם
       recordMeterReading כן עדכן, ה-effect ידרוס אותו לערך הנכון. */
    setAmounts((prev) => ({ ...prev, [meter]: Number(bill[meter]?.amount || 0) }));
    setPendingReadings((p) => { const n = { ...p }; delete n[meter]; return n; });
    setReadingErrors((p) => { const n = { ...p }; delete n[meter]; return n; });
    setReadingDrafts((p) => { const n = { ...p }; delete n[meter]; return n; });
  };

  /* ערך ה-₪ האמיתי של סעיף: amounts[k] כשאין קריאה ממתינה עליו; כשיש —
     השדה מציג בכוונה את מספר הקריאה עצמו (לא ₪), אז עד "אישור קריאות"
     הסכום הקובע הוא עדיין זה שכבר שמור בחשבון. בלי זה, "סה״כ לחיוב"
     היה סוכם מספרי קריאה כאילו הם שקלים. */
  const billAmount = (k) => (pendingReadings[k] ? Number(bill[k]?.amount || 0) : Number(amounts[k]) || 0);

  /* מנורמל לשתי ספרות: אבק צף בסכימה (123.000...01) היה גורם ל"לשקל" לקפוץ שקל שלם. */
  const total = Math.round(CONSUMPTION.reduce((t, k) => t + billAmount(k), 0) * 100) / 100;
  const original = consumptionOf(bill);
  const isPaid = original.isPaid;
  /* גבייה נועלת את העריכה: ברגע שנרשם תשלום (גם חלקי) הסכומים קפואים —
     עריכה אחרי הגבייה משכתבת בשקט את מה שהדייר כבר שילם. "ביטול
     הסימון" מאפס את התשלום שנרשם ופותח את העריכה מחדש. */
  const locked = CONSUMPTION.some((k) => itemPaid(bill[k]) > 0);
  const changed = CONSUMPTION.some((k) => billAmount(k) !== Number(bill[k]?.amount || 0));
  /* תשלום חלקי (קריאה שנוספה אחרי גבייה יצרה יתרה): מה ששולם ומה שנותר
     מוצגים במפורש, וכפתור הקבלה מדבר על היתרה — "התקבל 879" על חשבון
     שכבר שולם ממנו 853 נראה כאילו הולכים לרשום את הכול פעמיים. */
  const paidTotal = Math.round(CONSUMPTION.reduce((t, k) => t + itemPaid(bill[k]), 0) * 100) / 100;
  const due = Math.max(0, Math.round((total - paidTotal) * 100) / 100);

  /* הסעיפים שמוצגים: רק מה שנגבה דרך בעל הנכס. סעיף שהדייר משלם ישירות
     לספק לא מקבל כאן שדה הזנה — אין חשבון כזה לגבות. סכום שכבר נרשם
     בחודש הזה מוצג תמיד, כדי שהיסטוריה לא תינעל. ביוב הוא שדה ישן —
     רק עם נתון קיים. */
  const keys = CONSUMPTION.filter((k) => {
    const has = Number(bill[k]?.amount || 0) > 0 || itemPaid(bill[k]) > 0;
    if (k === "sewage") return has;
    return has || billsUtility(apt, data.settings, k);
  });

  /* הקריאה האחרונה של כל מונה עד סוף החודש המוצג — הסכום מוזן מול המספר
     הזה, ובלעדיו צריך לצאת באמצע ל"מונים וחשבונות" רק כדי להציץ בו.
     לביוב אין מונה משלו ולכן אין לו שורה כזו. */
  /* כשיש קריאה ממתינה על הסעיף (pendingReadings), הנוסחה חיה: הישנה
     היא הקריאה השמורה האחרונה, והחדשה היא מה שבשדה עכשיו (כולל תיקון
     ידני, לפני "אישור קריאות"). בלי קריאה ממתינה — הנוסחה על שתי
     הקריאות השמורות האחרונות, כמו קודם. */
  const readInfo = (meter) => {
    if (meter === "sewage") return null;
    /* גבול תחתון לפי תחילת התקופה בתוקף (readingFloor, כמו ב-
       consumptionForMonth): בלעדיו, הקריאה הראשונה של דייר חדש הושוותה
       לקריאת היציאה של קודמו — הפרש שלילי/חסר-משמעות שהוצג כ"קריאה
       אחרונה" בלי נוסחה, במקום כ"קריאה ראשונה". */
    const floor = readingFloor(data, apt.id, `${month}-28`);
    const list = readingsOf(data, apt.id, meter)
      .filter((r) => String(r.date).slice(0, 7) <= month)
      .filter((r) => !floor || r.date >= floor);
    const last = list[list.length - 1];
    if (!last) return null;
    const rate = rateFor(apt, data.settings, meter, last.date);
    const pending = pendingReadings[meter];
    const oldValue = pending ? last.value : (list[list.length - 2]?.value ?? null);
    const newValue = pending ? Number(readingDrafts[meter]) : last.value;
    if (oldValue == null) return { last, oldValue: null };
    const diff = Number.isFinite(newValue) ? Math.round((newValue - oldValue) * 10) / 10 : null;
    const cost = diff != null && diff > 0 ? Math.round(diff * rate * 100) / 100 : null;
    return { last, oldValue, newValue, diff, cost, rate };
  };

  /* איך התקבל התשלום. ברירת המחדל: מה שכבר נרשם על אחד הסעיפים, ואם אין —
     האמצעי שסוכם מול הדייר הזה (payMethodBills על התקופה, או ההגדרות). */
  const [method, setMethod] = useState(() =>
    CONSUMPTION.map((k) => bill[k]?.method).find(Boolean)
    || payMethodOf(null, data.settings, "elec", null, apt));

  /* עיגול בלחיצה — ההפרש נבלע בסעיף הגדול ביותר, כך שהסה"כ יוצא עגול
     והסעיפים האחרים נשארים כפי שחושבו מהמונים. */
  const roundTotal = (dir) => {
    if (locked) return;
    const target = roundBillAmount(total, dir);
    if (target === total || total <= 0) return;
    // רק בין סעיפים בלי קריאה ממתינה — עיגול לא אמור לגעת במספר קריאה.
    const roundable = keys.filter((k) => !pendingReadings[k]);
    if (!roundable.length) return;
    const biggest = roundable.reduce((best, k) => (billAmount(k) > billAmount(best) ? k : best), roundable[0]);
    /* שתי ספרות לכל היותר: הפרש צף (‎-0.4700000000000273) היה נשפך לשדה הקלט. */
    setAmounts({ ...amounts, [biggest]: Math.max(0, Math.round((billAmount(biggest) + (target - total)) * 100) / 100) });
  };

  const acts = (paid) => CONSUMPTION
    .filter((k) => billAmount(k) > 0 || Number(bill[k]?.amount || 0) > 0)
    .map((k) => ({
      type: "set_bill", apartment: apt.id, month, bill: k, tenancyId: tenancy.id,
      amount: billAmount(k),
      ...(paid === undefined ? {} : { paid }),
      // האמצעי נרשם רק כשמסמנים שהתקבל — שמירת סכומים לא נוגעת בו.
      ...(paid === true && method ? { method } : {}),
    }));

  const save = (paid) => { const list = acts(paid); if (list.length) run(list); onClose(); };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center sm:p-4" style={{ background: "rgba(20,38,43,0.45)" }} onClick={onClose}>
      <div dir="rtl" className="w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl p-5 max-h-full overflow-y-auto" style={{ background: C.card }} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-1">
          <h3 className="disp text-lg font-bold">חשבונות צריכה</h3>
          <button onClick={onClose} aria-label="סגירה" style={{ color: C.muted }}><X size={18} /></button>
        </div>
        <div className="flex items-center justify-between gap-2 mb-4">
          <div className="text-xs" style={{ color: C.muted }}>
            {apt.name}{tenancy.tenant ? ` · ${tenancy.tenant}` : ""} · {monthLabel(month)}
          </div>
          {tenancy.tenant && tenancy.phone && (
            <a href={waLink(tenancy.phone, consumptionBillsText({ ...apt, ...tenancy, id: apt.id }, bill, month, data.settings, data, messageMode({ ...apt, ...tenancy, id: apt.id }, data, month)))}
              target="_blank" rel="noreferrer"
              className="text-[11px] rounded-lg px-2.5 py-1.5 flex items-center gap-1 shrink-0" style={{ background: "#128C7E", color: "#fff" }}>
              <MessageCircle size={12} /> שליחת חשבונות
            </a>
          )}
        </div>

        <div className="space-y-2">
          {keys.length === 0 && (
            <div className="text-sm py-2" style={{ color: C.muted }}>
              כל סעיפי הצריכה בנכס הזה משולמים ישירות לספק — אין חשבון להזין.
            </div>
          )}
          {keys.map((k) => {
            const it = bill[k];
            const paidHere = it && Number(it.amount || 0) > 0 && itemDue(it) <= 0;
            const info = readInfo(k);
            const pending = pendingReadings[k];
            const readingErr = readingErrors[k];
            return (
              <div key={k}>
                <div className="flex items-center gap-2">
                  <span className="text-sm w-16">{BILL_LABEL[k]}</span>
                  {/* שדה הסכום (₪) — קטן וברור שזה כסף, לא קריאה. נעול
                      כשיש קריאה ממתינה: הסכום הסופי ייקבע רק באישור. */}
                  <div className="flex items-center gap-1 shrink-0">
                    <input type="number" inputMode="numeric" value={amounts[k] || ""} placeholder="0"
                      disabled={locked || pending}
                      title={locked ? "החשבון נגבה — נעול לעריכה. ביטול הסימון פותח אותו." : pending ? "הסכום ייקבע עם אישור הקריאה" : undefined}
                      onChange={(e) => setAmounts({ ...amounts, [k]: Number(e.target.value) || 0 })}
                      style={{
                        ...inputStyle, width: 60, padding: "6px 6px", fontSize: 14,
                        ...(locked || pending ? { background: "#F1F4F1", color: C.muted } : {}),
                      }} />
                    <span className="text-[11px]" style={{ color: C.muted }}>ש״ח</span>
                  </div>
                  {/* שדה הקריאה — נפרד לגמרי משדה הסכום, כדי שלא ייראה
                      כאילו מספר המונה הוא הסכום לתשלום. תמיד מציג את
                      הקריאה האחרונה שנשמרה; הקלדה בו פותחת קריאה ממתינה
                      חדשה. אין לביוב מונה משלו. */}
                  {k !== "sewage" && (
                    <div className="flex flex-col items-center shrink-0">
                      <input type="number" inputMode="decimal" placeholder="קריאה"
                        disabled={locked}
                        title={locked ? "החשבון נגבה — נעול לעריכה." : "קריאת המונה — הקלדה כאן פותחת קריאה חדשה"}
                        value={pending ? (readingDrafts[k] ?? "") : (info?.last?.value ?? "")}
                        onChange={(e) => onReadingChange(k, e.target.value)}
                        style={{
                          ...inputStyle, width: 68, padding: "6px 6px", fontSize: 13, textAlign: "center",
                          ...(locked ? { background: "#F1F4F1", color: C.muted } : pending ? { borderColor: C.ochre } : {}),
                        }} />
                      <span className="text-[9px] mt-0.5" style={{ color: pending ? "#6B4E12" : C.muted }}>קריאה</span>
                    </div>
                  )}
                  <span className="text-[11px] w-14 text-left" style={{ color: paidHere ? C.paid : C.muted }}>
                    {paidHere ? "שולם" : itemPaid(it) > 0 ? `שולם ${fmt(itemPaid(it))}` : ""}
                  </span>
                  {/* אישור נפרד לכל סעיף, ליד הקריאה עצמה — לא כפתור אחד
                      משותף למטה: ברור בדיוק מה נשמר בכל לחיצה. */}
                  {pending && (
                    <button type="button" title="אישור הקריאה הזו" onClick={() => confirmOne(k)}
                      className="rounded-lg p-1.5 flex items-center shrink-0" style={{ background: C.ochre, color: "#fff" }}>
                      <Check size={13} />
                    </button>
                  )}
                  {/* צילום מונה — הקריאה ממתינה לאישור למעלה, לא נשמרת
                      מיד. אין לביוב מונה. */}
                  {k !== "sewage" && setData && (
                    <MeterScanButtons apt={apt} meter={k} onScanned={onScanned} disabled={locked} />
                  )}
                </div>
                {readingErr && (
                  <div className="text-[11px] mt-0.5" style={{ color: C.late, paddingInlineStart: 72 }}>{readingErr}</div>
                )}
                {info && (
                  <div className="text-[11px] mt-0.5" style={{ color: C.muted, paddingInlineStart: 72, fontVariantNumeric: "tabular-nums" }}>
                    {info.oldValue == null ? (
                      <>קריאה ראשונה: {info.last.value} · {heDate(info.last.date)}</>
                    ) : info.cost != null ? (
                      <>
                        {BILL_LABEL[k]}{" "}
                        <bdi style={{ unicodeBidi: "isolate", direction: "ltr" }}>
                          {numX(info.oldValue)}-{numX(info.newValue)}={numX(info.diff)}*{info.rate}=
                          <b style={{ color: C.ink }}>{numX(info.cost)} ש"ח</b>
                        </bdi>
                      </>
                    ) : (
                      <>קריאה אחרונה: {info.last.value} · {heDate(info.last.date)}</>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className="mt-4 rounded-lg p-3 flex items-center justify-between" style={{ background: "#F5F8F7" }}>
          <div>
            <div className="text-[11px]" style={{ color: C.muted }}>סה״כ לחיוב</div>
            <div className="disp text-xl font-bold" style={{ color: CONS_COLOR }}>{nisX(total)}</div>
            {changed && original.amount > 0 && (
              <div className="text-[11px]" style={{ color: C.muted }}>היה {nisX(original.amount)}</div>
            )}
            {paidTotal > 0 && !isPaid && (
              <div className="text-[11px]" style={{ color: C.muted }}>
                שולם {nisX(paidTotal)} · <b style={{ color: C.late }}>נותרו {nisX(due)}</b>
              </div>
            )}
          </div>
          <div className="flex flex-col items-center gap-1">
            <div className="text-[11px]" style={{ color: C.muted }}>עיגול — כל לחיצה שקל</div>
            <div className="flex gap-1.5">
              {[[1, ArrowUpCircle, "כלפי מעלה"], [-1, ArrowDownCircle, "כלפי מטה"]].map(([dir, Icon, hint]) => {
                const target = roundBillAmount(total, dir);
                const dead = locked || total <= 0 || target === total;
                return (
                  <button key={hint} onClick={() => roundTotal(dir)} disabled={dead}
                    title={locked ? "החשבון נגבה — נעול לעריכה" : dead ? "עיגול אינו מאפס את החשבון" : `עיגול ${hint} — ${nisX(target)}`}
                    aria-label={`עיגול הסכום ${hint}`}
                    className="rounded-lg p-2 flex items-center justify-center"
                    style={{ border: `1px solid ${C.line}`, color: dead ? C.line : CONS_COLOR, background: "#fff" }}>
                    <Icon size={22} />
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {locked && (
          <div className="mt-3 rounded-lg p-2.5 text-[12px] flex items-center gap-1.5" style={{ background: "#F1F4F1", color: C.muted }}>
            <Lock size={13} className="shrink-0" />
            {isPaid
              ? 'החשבון נגבה ולכן נעול לעריכה. "ביטול הסימון" מאפס את התשלום שנרשם ופותח את העריכה מחדש.'
              : `החשבון נגבה חלקית ולכן הסכומים נעולים לעריכה. את היתרה (${nisX(due)}) מסמנים בכפתור למטה; "ביטול הסימון" מאפס את התשלום ופותח עריכה.`}
          </div>
        )}

        {/* איך התקבל — כמו בשכר הדירה ובוועד: הסימון רושם את האמצעי,
            וברירת המחדל היא מה שסוכם מול הדייר. */}
        {!isPaid && (
          <div className="mt-3 flex items-center gap-2 text-[12px]" style={{ color: C.muted }}>
            איך התקבל?
            <MethodPick value={method} onChange={(v) => setMethod(v || null)} />
          </div>
        )}

        <div className="mt-4 flex gap-2">
          {isPaid ? (
            <button onClick={() => save(false)} className="flex-1 rounded-lg py-2.5 text-sm"
              style={{ border: `1px solid ${C.line}`, color: C.muted }}>
              ביטול הסימון
            </button>
          ) : (
            <button onClick={() => save(true)} disabled={total <= 0} className="flex-1 rounded-lg py-2.5 text-sm flex items-center justify-center gap-1.5"
              style={{ background: CONS_COLOR, color: "#fff", opacity: total > 0 ? 1 : 0.5 }}>
              <Check size={15} /> {paidTotal > 0 ? `התקבלה היתרה ${nisX(due)}` : `התקבל ${nisX(total)}`}
            </button>
          )}
          {/* בגבייה חלקית "שמירת הסכומים" ממילא מתה (הסכומים נעולים) —
              במקומה עומד "ביטול הסימון", שעד עכשיו הופיע רק בתשלום מלא. */}
          {locked && !isPaid ? (
            <button onClick={() => save(false)} className="rounded-lg px-4 py-2.5 text-sm"
              style={{ border: `1px solid ${C.line}`, color: C.muted }}>
              ביטול הסימון
            </button>
          ) : !locked && (
            <button onClick={() => save(undefined)} disabled={!changed} className="rounded-lg px-4 py-2.5 text-sm"
              style={{ border: `1px solid ${C.teal}`, color: C.teal, opacity: changed ? 1 : 0.5 }}>
              שמירת הסכומים
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/* ============================================================================
   היסטוריית חשבונות צריכה — לקריאה בלבד. נפתחת בלחיצה על שם הדייר בלוח
   "חשבונות צריכה"; עצמאית מהמתג "ניווט לחיץ בדשבורד" (כבוי כברירת
   מחדל) — זו לא ניווט למסך אחר, זו יכולת חדשה שרלוונטית תמיד. מציגה
   קריאות מונה + סכומים חודשיים; ההזנה עצמה (כולל צילום מונה) נשארת רק
   ב-ConsumptionModal (לחיצה על תא) ובמסך "מונים".
   ============================================================================ */
function ConsumptionHistoryPanel({ apt, tenancy, data, onClose }) {
  const meters = ["elec", "water", "gas"].filter((k) =>
    billsUtility(apt, data.settings, k) || readingsOf(data, apt.id, k).length > 0);

  const months = useMemo(() => {
    const out = [];
    let mk = CUR_KEY;
    for (let i = 0; i < 12; i++) { out.push(mk); mk = prevMonth(mk); }
    return out;
  }, []);

  const mergedApt = { ...apt, ...tenancy, id: apt.id };
  const monthlyRows = months
    .map((mk) => ({ mk, ...consumptionOf(data.bills[rowKey(apt.id, mk)]) }))
    .filter((r) => r.amount > 0 || r.paid > 0);

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center sm:p-4" style={{ background: "rgba(20,38,43,0.45)" }} onClick={onClose}>
      <div dir="rtl" className="w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl p-5 max-h-full overflow-y-auto" style={{ background: C.card }} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-1">
          <h3 className="disp text-lg font-bold">היסטוריית חשבונות צריכה</h3>
          <button onClick={onClose} aria-label="סגירה" style={{ color: C.muted }}><X size={18} /></button>
        </div>
        <div className="flex items-center justify-between gap-2 mb-4">
          <div className="text-xs" style={{ color: C.muted }}>{apt.name}{tenancy.tenant ? ` · ${tenancy.tenant}` : ""}</div>
          {tenancy.tenant && tenancy.phone && (
            <a href={waLink(tenancy.phone, consumptionBillsText(mergedApt, data.bills[rowKey(apt.id, CUR_KEY)] || emptyBill(), CUR_KEY, data.settings, data, messageMode(mergedApt, data, CUR_KEY)))}
              target="_blank" rel="noreferrer"
              className="text-[11px] rounded-lg px-2.5 py-1.5 flex items-center gap-1 shrink-0" style={{ background: "#128C7E", color: "#fff" }}>
              <MessageCircle size={12} /> שליחת חשבונות
            </a>
          )}
        </div>

        {meters.map((k) => {
          /* אותו readingFloor שמשמש את הנוסחה החיה בחלון החודש הבודד —
             בלי זה, הקריאה הראשונה של הדייר הזה הייתה מחושבת מול קריאת
             היציאה של קודמו. */
          const floor = readingFloor(data, apt.id, TODAY);
          const all = readingsOf(data, apt.id, k).filter((r) => !floor || r.date >= floor);
          const rows = all.slice(1).map((r, i) => {
            const prev = all[i];
            const qty = Math.round((r.value - prev.value) * 10) / 10;
            const rate = rateFor(apt, data.settings, k, r.date);
            const cost = qty > 0 ? Math.round(qty * rate * 100) / 100 : 0;
            return { id: r.id, date: r.date, prevValue: prev.value, value: r.value, qty, rate, cost };
          }).reverse().slice(0, 8);
          return (
            <div key={k} className="mb-4">
              <div className="text-xs mb-1.5" style={{ fontWeight: 700, color: C.ink }}>{BILL_LABEL[k]}</div>
              {rows.length === 0 ? (
                <div className="text-[11px]" style={{ color: C.muted }}>
                  {all.length === 0 ? "עדיין אין קריאות." : `קריאה ראשונה: ${all[0].value} ${UNIT[k]} · ${heDate(all[0].date)}`}
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-[11px]" style={{ borderCollapse: "collapse", whiteSpace: "nowrap" }}>
                    <thead>
                      <tr style={{ color: C.muted }}>
                        <th className="text-right font-normal pb-1 pl-2">תאריך</th>
                        <th className="text-right font-normal pb-1 pl-2">קריאה קודמת</th>
                        <th className="text-right font-normal pb-1 pl-2">קריאה</th>
                        <th className="text-right font-normal pb-1 pl-2">סה״כ</th>
                        <th className="text-right font-normal pb-1 pl-2">מחיר יח׳</th>
                        <th className="text-right font-normal pb-1">סה״כ מחיר</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y" style={{ borderColor: C.line }}>
                      {rows.map((r) => (
                        <tr key={r.id}>
                          <td className="py-1 pl-2" style={{ color: C.muted }}>{heDate(r.date)}</td>
                          <td className="py-1 pl-2" style={{ fontVariantNumeric: "tabular-nums" }}>{r.prevValue}</td>
                          <td className="py-1 pl-2" style={{ fontVariantNumeric: "tabular-nums" }}>{r.value}</td>
                          <td className="py-1 pl-2" style={{ fontVariantNumeric: "tabular-nums" }}>{r.qty} {UNIT[k]}</td>
                          {/* nis() מעגל לשקל שלם — טוב לסכום חשבון, לא
                              לתעריף (0.65 היה מוצג כ-"₪1"). numX שומר
                              עשרוני, כמו בשורה האפורה ובהודעת הוואטסאפ. */}
                          <td className="py-1 pl-2" style={{ fontVariantNumeric: "tabular-nums" }}>{`₪${numX(r.rate)}`}</td>
                          <td className="py-1" style={{ fontWeight: 700, fontVariantNumeric: "tabular-nums", color: CONS_COLOR }}>{`₪${numX(r.cost)}`}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          );
        })}

        <div className="text-xs mt-3 mb-1.5" style={{ fontWeight: 700, color: C.ink }}>סכומים חודשיים</div>
        {monthlyRows.length === 0 ? (
          <div className="text-[11px]" style={{ color: C.muted }}>אין עדיין חשבונות רשומים.</div>
        ) : (
          <div className="divide-y" style={{ borderColor: C.line }}>
            {monthlyRows.map((r) => (
              <div key={r.mk} className="flex items-center justify-between py-1.5 text-[12px]">
                <span style={{ color: C.muted }}>{monthLabel(r.mk)}</span>
                <b style={{ fontVariantNumeric: "tabular-nums", color: r.isPaid ? C.paid : CONS_COLOR }}>
                  {nis(r.amount)}{r.isPaid ? " · שולם" : r.paid > 0 ? ` · נותרו ${nis(r.due)}` : ""}
                </b>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/* ============================================================================
   הזנת סכום שונה משכר הדירה/ועד הבית הצפוי — נפתח מהעיפרון בתא.

   נועד בעיקר לתשלומים מהעבר: דייר שכבר גר בנכס ורק עכשיו נרשם במערכת
   (תאריך כניסה בעבר, ראו backdateNote ב-change_tenant), וסכום שנגבה
   בפועל בחודש מסוים שונה מהרגיל. הפעולה זהה לזו שהלחיצה הרגילה שולחת —
   רק עם סכום ואמצעי תשלום שנבחרו, במקום הסכום הצפוי וברירת המחדל.
   ============================================================================ */
const EDIT_LABEL = { rent: "שכר דירה", vaad: "ועד בית" };

function AmountEditPopup({ apt, tenancy, kind, month, initialAmount, data, run, onClose }) {
  const [amount, setAmount] = useState(String(initialAmount || ""));
  const [method, setMethod] = useState(() => payMethodOf(null, data.settings, kind, null, apt));
  const color = kind === "vaad" ? VAAD_COLOR : C.paid;
  const n = Number(amount) || 0;

  const confirm = () => {
    if (!(n > 0)) return;
    const base = { apartment: apt.id, month, paid: true, tenancyId: tenancy?.id, method };
    run([kind === "vaad"
      ? { type: "set_bill", bill: "vaad", amount: n, ...base }
      : { type: "set_rent_paid", amount: n, ...base }]);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center sm:p-4" style={{ background: "rgba(20,38,43,0.45)" }} onClick={onClose}>
      <div dir="rtl" className="w-full sm:max-w-xs rounded-t-2xl sm:rounded-2xl p-5" style={{ background: C.card }} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-1">
          <h3 className="disp text-lg font-bold">{EDIT_LABEL[kind]}</h3>
          <button onClick={onClose} aria-label="סגירה" style={{ color: C.muted }}><X size={18} /></button>
        </div>
        <div className="text-xs mb-4" style={{ color: C.muted }}>
          {apt.name}{tenancy?.tenant ? ` · ${tenancy.tenant}` : ""} · {monthLabel(month)}
        </div>

        <label className="text-xs" style={{ color: C.muted }}>כמה התקבל בפועל?</label>
        <input type="number" inputMode="numeric" autoFocus value={amount} placeholder="0"
          onChange={(e) => setAmount(e.target.value)}
          style={{ ...inputStyle, width: "100%", padding: "8px 10px", fontSize: 16, marginTop: 4 }} />

        <div className="mt-3 flex items-center gap-2 text-[12px]" style={{ color: C.muted }}>
          איך התקבל?
          <MethodPick value={method} onChange={(v) => setMethod(v || null)} />
        </div>

        <button onClick={confirm} disabled={!(n > 0)}
          className="mt-4 w-full rounded-lg py-2.5 text-sm flex items-center justify-center gap-1.5"
          style={{ background: color, color: "#fff", opacity: n > 0 ? 1 : 0.5 }}>
          <Check size={15} /> אישור — התקבל {n > 0 ? nis(n) : ""}
        </button>
      </div>
    </div>
  );
}

export default function CollectionBoards({ data, setData, year, setYear, run, onOpenProperty = null, onOpenMeters = null }) {
  const [consCell, setConsCell] = useState(null);   // { aptId, tenancyId, month }
  const [editCell, setEditCell] = useState(null);   // { kind: "rent"|"vaad", aptId, tenancyId, month, amount }
  const [consHistory, setConsHistory] = useState(null);   // { aptId, tenancyId } — לחיצה על שם הדייר בלוח הצריכה
  const onOpenConsHistory = (aptId, tenancyId) => setConsHistory({ aptId, tenancyId });
  // הגדרה ב"תעריפים ותנאים" — כבויה כברירת מחדל, כדי לא לשנות התנהגות קיימת בלי בחירה מפורשת.
  const clickableNames = !!(data.settings?.clickablePropertyNames && onOpenProperty && onOpenMeters);
  const narrow = useNarrow();
  const dim = narrow ? MOBILE : DESKTOP;

  const allMonths = useMemo(() => MONTHS_SHORT.map((_, i) => i + 1), []);
  /* בטלפון מוצג חודש אחד. מתחילים מהחודש הנוכחי — זה מה שגובים עכשיו. */
  const [mobileMonth, setMobileMonth] = useState(CUR_M);
  const months = narrow ? [mobileMonth] : allMonths;
  const stepMonth = (delta) => {
    let m = mobileMonth + delta;
    if (m < 1) { m = 12; setYear(year - 1); }
    if (m > 12) { m = 1; setYear(year + 1); }
    setMobileMonth(m);
  };

  /* dueDay — היום בחודש שממנו תשלום פתוח נחשב באיחור. בלעדיו החודש
     הנוכחי נשאר "לבן" גם כשה-KPI למעלה כבר סופר אותו כחוב: דנה עם יום
     תשלום 1 נראתה בסדר בלוח כל החודש. */
  const flags = (m, dueDay = 32) => {
    const cur = year === CUR_Y && m === CUR_M;
    return {
      future: year > CUR_Y || (year === CUR_Y && m > CUR_M),
      past: year < CUR_Y || (year === CUR_Y && m < CUR_M) || (cur && now.getDate() > Number(dueDay || 32)),
    };
  };

  const EMPTY_CELL = (mk) => ({ mk, amount: 0, paid: false, partial: 0, collected: 0 });
  /* כל תקופה עם דייר שחופפת לשנה המוצגת, מהוותיקה לחדשה — tenanciesOf
     כבר ממיינת כך. בלי אף תקופה כזו: שורה וירטואלית יחידה (null) —
     "פנויה", בדיוק כמו היום. */
  const yearStart = `${year}-01-01`, yearEnd = `${year}-12-31`;
  const tenancyRowsOf = (aptId) => {
    const list = tenanciesOf(data, aptId)
      .filter((t) => t.tenant && t.leaseStart <= yearEnd && (!tenancyEnd(t) || tenancyEnd(t) >= yearStart));
    /* השורה הווירטואלית "פנויה" מתווספת כשאין תקופה בתוקף היום —
       גם לנכס שדיירו עזב במרץ ועדיין לא הושכר: בלעדיה השורה ההיסטורית
       שלו נראית כאילו הדייר עדיין שם, ורמז הדייר הבא נעלם.
       חריג: אם ב-list כבר יש תקופה עתידית (דייר שנרשם וטרם נכנס —
       leaseStart בעתיד אך בתוך השנה המוצגת), לתקופה הזו כבר יש שורה
       משלה עם תג "נכנס" — הוספת השורה הווירטואלית הייתה כופלת את אותו
       דייר פעמיים (השורה שלו + רמז "הדייר הבא" עליו עצמו). */
    const hasUpcomingInList = list.some((t) => t.leaseStart > TODAY);
    return currentTenancy(data, aptId) || hasUpcomingInList ? list : [...list, null];
  };

  const shortDate = (iso) => {
    if (!iso) return "";
    const [, m, day] = iso.split("-");
    return `${Number(day)}.${Number(m)}`;
  };
  /* "בוטל" אינו שדה נפרד — נגזר מ-close_tenancy הקיימת: אם תאריך הסגירה
     חל לפני שהדייר בכלל נכנס (או באותו יום), התקופה מעולם לא הייתה
     פעילה בפועל, ולכן זה ביטול ולא עזיבה. תקופה שפג תוקפה מעצמה (בלי
     סגירה מפורשת) היא תמיד "עזב" — leaseEnd שלה תמיד אחרי leaseStart. */
  const tenancyStatus = (tenancy) => {
    if (!tenancy) return null;
    const end = tenancyEnd(tenancy);
    const over = tenancy.closed || (end && end < TODAY);
    if (!over) return "active";
    return tenancy.closed && end && tenancy.leaseStart && end <= tenancy.leaseStart ? "cancelled" : "departed";
  };
  const [rentFilter, setRentFilter] = useState(DEFAULT_STATUS_FILTER);
  const [vaadFilter, setVaadFilter] = useState(DEFAULT_STATUS_FILTER);
  const [consFilter, setConsFilter] = useState(DEFAULT_STATUS_FILTER);
  const statusMatches = (tenancy, filter) => !tenancy || !!filter[tenancyStatus(tenancy)];

  /* --- שכר דירה ---
     כל שורה היא תקופה אחת; הסכום מחושב ישירות ממנה (effectiveRent),
     בלי לבדוק אם היא "הדייר הנוכחי" — כל תקופה שחופפת לשנה מקבלת שורה
     משלה, כולל תקופה שכבר הוחלפה. */
  const rentCell = (apt, tenancy, m) => {
    const mk = mKey(year, m);
    if (!tenancy) return EMPTY_CELL(mk);
    const charged = activeIn(tenancy, year, m) ? Number(effectiveRent(tenancy, mk)) || 0 : 0;
    const paidAmt = rentPaidOf(data, apt.id, mk, tenancy.id);
    const amount = charged || (paidAmt > 0 ? paidAmt : 0);
    const paid = amount > 0 && paidAmt >= amount;
    return { mk, amount, paid, partial: !paid && paidAmt > 0 ? Math.max(0, amount - paidAmt) : 0, collected: paidAmt };
  };

  /* --- ועד בית --- */
  const vaadCell = (apt, tenancy, m) => {
    const mk = mKey(year, m);
    if (!tenancy) return EMPTY_CELL(mk);
    const primary = tenancyForMonth(data, apt.id, mk)?.id === tenancy.id;
    const it = entryFor(data.bills[rowKey(apt.id, mk)]?.vaad, tenancy.id, primary);
    const lived = activeIn(tenancy, year, m);
    const expected = lived && tenancy.vaadActive !== false ? effectiveVaad(tenancy, mk) : 0;
    const amount = Number(it?.amount || 0) > 0 ? Number(it.amount) : expected;
    const paidAmt = itemPaid(it);
    const paid = amount > 0 && it && itemDue(it) <= 0 && Number(it.amount || 0) > 0;
    return { mk, amount, paid, partial: !paid && paidAmt > 0 ? Math.max(0, amount - paidAmt) : 0, collected: paidAmt };
  };

  /* --- צריכה --- */
  /* האם משהו מהצריכה בכלל נגבה דרך בעל הנכס. תא "להזנה" מוצע רק אז —
     כשהכול משולם ישירות לספק אין חשבון להזין, והתא נשאר "אין חיוב". */
  /* מי משלם למי הוא הגדרה ברמת הנכס (סוג נכס, מונים) ולא ברמת התקופה —
     נשאר תלוי ב-apt, לא tenancy. */
  const consBilled = (apt) => ["elec", "water", "gas"].some((k) => billsUtility(apt, data.settings, k));
  const consCellOf = (apt, tenancy, m) => {
    const mk = mKey(year, m);
    if (!tenancy) return { ...EMPTY_CELL(mk), open: false };
    const primary = tenancyForMonth(data, apt.id, mk)?.id === tenancy.id;
    const rawBill = data.bills[rowKey(apt.id, mk)];
    const b = rawBill ? Object.fromEntries(CONSUMPTION.map((k2) => [k2, entryFor(rawBill[k2], tenancy.id, primary)])) : rawBill;
    const c = consumptionOf(b);
    const lived = activeIn(tenancy, year, m);
    return { mk, amount: c.amount, paid: c.isPaid, partial: !c.isPaid && c.paid > 0 ? c.due : 0, collected: c.paid, open: lived && (c.amount > 0 || consBilled(apt)) };
  };

  /* ============ מי מופיע באיזה לוח ============
     נכס בלי ועד בית לא שייך ללוח ועד הבית, ונכס שחשבונות הצריכה שלו
     משולמים ישירות לספק (או בלי חיוב) לא שייך ללוח הצריכה — שורה ריקה
     רק מבלבלת. נכס נשאר בלוח אם נרשמו בו סכומים בשנה המוצגת (דייר
     קודם ששילם דרך בעל הנכס), כדי שהיסטוריה לא תיעלם. */
  /* נכס נשאר בלוח אם *לתקופה הזו* נרשמו סכומים בשנה המוצגת (גם אם היא
     כבר לא הראשית באותו חודש — אחרת החלק של תקופה משנית ייעלם מהסינון). */
  const recordedIn = (apt, tenancy, keys) => allMonths.some((m) => {
    const mk = mKey(year, m);
    const primary = tenancyForMonth(data, apt.id, mk)?.id === tenancy.id;
    const bill = data.bills[rowKey(apt.id, mk)];
    return keys.some((k) => Number(entryFor(bill?.[k], tenancy.id, primary)?.amount || 0) > 0);
  });
  const hasVaad = (apt, tenancy) => !!tenancy && ((tenancy.vaadActive !== false && Number(tenancy.vaad || 0) > 0) || recordedIn(apt, tenancy, ["vaad"]));
  const hasConsumption = (apt, tenancy) => !!tenancy && (consBilled(apt) || recordedIn(apt, tenancy, CONSUMPTION));

  const totalsOf = (cellFn, rowList) => months.map((m) => rowList.reduce((t, r) => {
    const c = cellFn(r.apt, r.tenancy, m);
    return { expected: t.expected + c.amount, paid: t.paid + Math.min(c.collected, c.amount || c.collected) };
  }, { expected: 0, paid: 0 }));

  const labelStyle = dim.labelW ? { width: dim.labelW } : { flex: 1, minWidth: 0 };
  /* השורה נושאת את הדייר של *התקופה הזו*, לא בהכרח הדייר הנוכחי של
     הנכס — כל תקופה שחופפת לשנה המוצגת מקבלת שורה משלה. */
  const label = (apt, tenancy, navKind) => {
    const next = !tenancy && upcomingTenancies(data, apt.id)[0];
    /* "cons-history" הוא נתיב חדש ועצמאי — פותח את היסטוריית הצריכה
       לקריאה בלבד, ולא כפוף למתג "ניווט לחיץ בדשבורד" (כבוי כברירת
       מחדל): זה לא ניווט לעמוד אחר, זו יכולת חדשה שרלוונטית תמיד. */
    const isHistoryNav = navKind === "cons-history";
    const nav = navKind === "props" ? onOpenProperty : navKind === "meters" ? onOpenMeters : isHistoryNav ? onOpenConsHistory : null;
    const navActive = !!nav && (isHistoryNav || clickableNames) && !!tenancy;
    const nameSize = tenancy ? "text-[11px]" : "text-sm";
    const nameWeight = tenancy ? 400 : 600;
    /* בלוח הצריכה (cons-history) הלחיצה היא על שם *הדייר* — זה מה שהמשתמש
       מבקש ("לוחץ על שם הדייר"). בלוחות אחרים (props/meters) הלחיצה
       נשארת על שם הנכס, כמו קודם — לא לגעת בהתנהגות שלהם. */
    const nameEl = navActive && !isHistoryNav ? (
      <button type="button" onClick={() => nav(apt.id, tenancy.id)}
        className={`${nameSize} truncate block w-full text-right`}
        style={{ fontWeight: nameWeight, color: C.teal, background: "none", border: "none", padding: 0, cursor: "pointer" }}>
        {apt.name}
      </button>
    ) : (
      <div className={`${nameSize} truncate`} style={{ fontWeight: nameWeight, color: tenancy ? C.muted : C.ink }}>{apt.name}</div>
    );
    const status = tenancy && tenancyStatus(tenancy);
    const end = tenancy && tenancyEnd(tenancy);
    /* "יצא"/"נכנס" מוצגים רק כשהחודש המוצג הוא בפועל חודש העזיבה/הכניסה —
       בטלפון (חודש בודד) זה מדויק; במחשב (כל השנה בשורה) זה תמיד מתקיים
       כל עוד האירוע קרה בתוך השנה המוצגת, בדיוק כמו התג הישן שהוא מחליף. */
    const inShownMonths = (iso) => !!iso && months.some((m) => mKey(year, m) === iso.slice(0, 7));
    const justLeft = status === "departed" && inShownMonths(end);
    const justEntered = status === "active" && inShownMonths(tenancy.leaseStart);
    return (
      <div className="pl-3 shrink-0" style={labelStyle}>
        {tenancy && (navActive && isHistoryNav ? (
          <button type="button" onClick={() => nav(apt.id, tenancy.id)}
            className="text-sm truncate flex items-center gap-1 w-full text-right"
            style={{ fontWeight: 600, color: C.teal, background: "none", border: "none", padding: 0, cursor: "pointer" }}>
            {tenancy.tenant}
            {justLeft ? (
              <span className="text-[10px] shrink-0 flex items-center gap-0.5" style={{ color: C.late, fontWeight: 700 }}>
                <ArrowRight size={11} /> יצא
              </span>
            ) : justEntered ? (
              <span className="text-[10px] shrink-0 flex items-center gap-0.5" style={{ color: C.paid, fontWeight: 700 }}>
                <ArrowLeft size={11} /> נכנס
              </span>
            ) : status !== "active" && (
              <span className="text-[10px] shrink-0" style={{ color: C.muted, fontWeight: 400 }}>
                · {status === "cancelled" ? "בוטל/ה" : "עזב/ה"}{end ? ` · ${shortDate(end)}` : ""}
              </span>
            )}
          </button>
        ) : (
          <div className="text-sm truncate flex items-center gap-1" style={{ fontWeight: 600 }}>
            {tenancy.tenant}
            {justLeft ? (
              <span className="text-[10px] shrink-0 flex items-center gap-0.5" style={{ color: C.late, fontWeight: 700 }}>
                <ArrowRight size={11} /> יצא
              </span>
            ) : justEntered ? (
              <span className="text-[10px] shrink-0 flex items-center gap-0.5" style={{ color: C.paid, fontWeight: 700 }}>
                <ArrowLeft size={11} /> נכנס
              </span>
            ) : status !== "active" && (
              <span className="text-[10px] shrink-0" style={{ color: C.muted, fontWeight: 400 }}>
                · {status === "cancelled" ? "בוטל/ה" : "עזב/ה"}{end ? ` · ${shortDate(end)}` : ""}
              </span>
            )}
          </div>
        ))}
        {nameEl}
        {!tenancy && (
          <div className="text-[11px] truncate" style={{ color: next ? C.muted : C.ochre }}>
            {next ? `${next.tenant} מ-${heDate(next.leaseStart)}` : "פנויה"}
          </div>
        )}
      </div>
    );
  };

  /* בטלפון (חודש בודד): שורה בלי שום פעילות באותו חודש לא מוצגת, כדי
     לא לתפוס מקום עם שורה ריקה לגמרי — חוץ מהשורה הווירטואלית "פנויה"
     שתמיד מוצגת. */
  const rows = (cellFn, color, onCell, opts = {}) => (opts.rowList || allRows)
    .filter((r) => !narrow || !r.tenancy || months.some((m) => cellFn(r.apt, r.tenancy, m).amount > 0 || activeIn(r.tenancy, year, m)))
    .map((r) => {
      // "שלח תזכורת" — ליד תא הלחיצה-להתקבל עצמו, לא ליד השם. רק כשיש
      // דייר עם טלפון, בלוחות שכ"ד/ועד בלבד (opts.waReminder).
      const reminderText = opts.waReminder && r.tenancy?.tenant && r.tenancy?.phone ? opts.waReminder(r.apt, r.tenancy) : null;
      return (
      <div key={`${r.apt.id}|${r.tenancy?.id || "vacant"}`} className="flex items-center gap-1 py-1">
        {label(r.apt, r.tenancy, opts.nav)}
        {months.map((m) => {
          const c = cellFn(r.apt, r.tenancy, m), f = flags(m, opts.dueDay ? opts.dueDay(r.apt) : undefined);
          return (
            <Cell key={m} amount={c.amount} paid={c.paid} partial={c.partial} past={f.past} future={f.future}
              color={color} dim={dim} disabled={opts.disableFuture !== false && f.future} open={!!c.open && !f.future}
              action={opts.action || null}
              title={`${r.apt.name}${r.tenancy ? ` · ${r.tenancy.tenant}` : ""} · ${MONTHS[m - 1]} ${year} · ${c.amount > 0 ? nis(c.amount) : "אין חשבון — לחיצה להזנה"}${c.paid ? " · שולם" : ""}`}
              onClick={() => onCell(r, c)} onEdit={opts.onEdit ? () => opts.onEdit(r, c) : undefined} />
          );
        })}
        {reminderText && (
          <a href={waLink(r.tenancy.phone, reminderText)} target="_blank" rel="noreferrer"
            title="שליחת תזכורת תשלום בוואטסאפ" aria-label="שליחת תזכורת תשלום"
            className="shrink-0 rounded-full flex items-center justify-center" style={{ width: 22, height: 22, background: "#128C7E", color: "#fff" }}>
            <MessageCircle size={12} />
          </a>
        )}
      </div>
      );
    });

  const allRows = useMemo(
    () => data.apartments.flatMap((apt) => tenancyRowsOf(apt.id).map((tenancy) => ({ apt, tenancy }))),
    [data.apartments, data.tenancies, year],
  );
  const rentRows = allRows.filter((r) => statusMatches(r.tenancy, rentFilter));
  const vaadRows = allRows.filter((r) => hasVaad(r.apt, r.tenancy) && statusMatches(r.tenancy, vaadFilter));
  const consRows = allRows.filter((r) => hasConsumption(r.apt, r.tenancy) && statusMatches(r.tenancy, consFilter));
  const consApt = consCell ? data.apartments.find((a) => a.id === consCell.aptId) : null;
  const consTenancy = consCell ? consRows.find((r) => r.apt.id === consCell.aptId && r.tenancy?.id === consCell.tenancyId)?.tenancy : null;
  const historyApt = consHistory ? data.apartments.find((a) => a.id === consHistory.aptId) : null;
  const historyTenancy = consHistory ? consRows.find((r) => r.apt.id === consHistory.aptId && r.tenancy?.id === consHistory.tenancyId)?.tenancy : null;
  const editApt = editCell ? data.apartments.find((a) => a.id === editCell.aptId) : null;
  const editRowList = editCell?.kind === "vaad" ? vaadRows : rentRows;
  const editTenancy = editCell ? editRowList.find((r) => r.apt.id === editCell.aptId && (r.tenancy?.id || null) === editCell.tenancyId)?.tenancy : null;

  return (
    <section className="mt-6 rounded-xl border overflow-hidden" style={{ background: C.card, borderColor: C.line }}>
      <div className="flex items-center justify-between gap-2 px-4 sm:px-5 py-4 border-b" style={{ borderColor: C.line }}>
        <div className="min-w-0">
          <h2 className="disp text-xl font-bold">לוחות גבייה</h2>
          <p className="text-xs mt-0.5 hidden sm:block" style={{ color: C.muted }}>בכל תא הסכום לתשלום. לחיצה מסמנת שהתקבל; בצריכה — פותחת פירוט עם עיגול</p>
          <p className="text-xs mt-0.5 sm:hidden" style={{ color: C.muted }}>לחיצה על סכום מסמנת שהתקבל</p>
        </div>
        {narrow ? (
          /* בטלפון: ניווט לפי חודש. החיצים גולשים לשנה הבאה/הקודמת בקצוות.
             ב-RTL הימני (‹ ימינה) חוזר אחורה והשמאלי מתקדם — כמו במונים. */
          <div className="flex items-center gap-1 shrink-0">
            <button onClick={() => stepMonth(-1)} aria-label="חודש קודם" className="w-9 h-9 rounded-lg flex items-center justify-center border" style={{ borderColor: C.line, color: C.muted }}><ChevronRight size={18} /></button>
            <div className="text-center" style={{ width: 84 }}>
              <div className="disp text-base font-bold leading-tight">{MONTHS[mobileMonth - 1]}</div>
              <div className="text-[11px] leading-tight" style={{ color: C.muted }}>{year}</div>
            </div>
            <button onClick={() => stepMonth(1)} aria-label="חודש הבא" className="w-9 h-9 rounded-lg flex items-center justify-center border" style={{ borderColor: C.line, color: C.muted }}><ChevronLeft size={18} /></button>
          </div>
        ) : (
          <div className="flex items-center gap-1">
            <button onClick={() => setYear(year + 1)} aria-label="שנה הבאה" className="w-8 h-8 rounded-lg flex items-center justify-center border" style={{ borderColor: C.line, color: C.muted }}><ChevronLeft size={16} /></button>
            <span className="disp text-lg font-bold w-14 text-center">{year}</span>
            <button onClick={() => setYear(year - 1)} aria-label="שנה קודמת" className="w-8 h-8 rounded-lg flex items-center justify-center border" style={{ borderColor: C.line, color: C.muted }}><ChevronRight size={16} /></button>
          </div>
        )}
      </div>

      <div className={narrow ? "" : "overflow-x-auto"}><div className={`${narrow ? "" : "min-w-[840px]"} px-4 sm:px-5 py-4`}>
        {!narrow && (
          <div className="flex items-center gap-1 mb-1 pr-[152px]">
            {MONTHS_SHORT.map((m) => <div key={m} className="text-center text-[11px] shrink-0" style={{ width: 52, color: C.muted }}>{m}</div>)}
          </div>
        )}

        <Board title="שכר דירה" hint="לחיצה מסמנת שהתקבל · עיפרון בפינה — סכום אחר" color={C.paid} dim={dim}
          totals={totalsOf(rentCell, rentRows)} filterSlot={<StatusFilter value={rentFilter} onChange={setRentFilter} />}
          rows={rows(rentCell, C.paid, (r, c) => run([{ type: "set_rent_paid", apartment: r.apt.id, month: c.mk, amount: c.amount, paid: !c.paid, tenancyId: r.tenancy?.id }]), {
            rowList: rentRows, dueDay: (a) => rentDayOf(a, data.settings), action: "לחיצה — התקבל", nav: "props",
            onEdit: (r, c) => setEditCell({ kind: "rent", aptId: r.apt.id, tenancyId: r.tenancy?.id || null, month: c.mk, amount: c.amount }),
            waReminder: (apt, tenancy) => rentDebtReminderText({ ...apt, ...tenancy, id: apt.id }, data, tenancy.id),
          })} />

        <Board title="ועד בית" hint="רק נכסים שמוגדר בהם ועד בית · לחיצה מסמנת שהתקבל · עיפרון בפינה — סכום אחר" color={VAAD_COLOR} dim={dim}
          totals={totalsOf(vaadCell, vaadRows)} empty="אין נכס עם ועד בית שנגבה דרכך." filterSlot={<StatusFilter value={vaadFilter} onChange={setVaadFilter} />}
          rows={rows(vaadCell, VAAD_COLOR, (r, c) => run([{ type: "set_bill", apartment: r.apt.id, month: c.mk, bill: "vaad", amount: c.amount, paid: !c.paid, tenancyId: r.tenancy?.id }]), {
            rowList: vaadRows, dueDay: (a) => billsDayOf(a, data.settings), action: "לחיצה — התקבל", nav: "meters",
            onEdit: (r, c) => setEditCell({ kind: "vaad", aptId: r.apt.id, tenancyId: r.tenancy?.id || null, month: c.mk, amount: c.amount }),
            waReminder: (apt, tenancy) => vaadDebtReminderText({ ...apt, ...tenancy, id: apt.id }, data, tenancy.id),
          })} />

        <Board title="חשבונות צריכה" hint="רק נכסים שהצריכה בהם משולמת לך · לחיצה פותחת פירוט, עיגול וסימון" color={CONS_COLOR} dim={dim}
          totals={totalsOf(consCellOf, consRows)} empty="אין נכס שחשבונות הצריכה שלו נגבים דרכך." filterSlot={<StatusFilter value={consFilter} onChange={setConsFilter} />}
          rows={rows(consCellOf, CONS_COLOR, (r, c) => setConsCell({ aptId: r.apt.id, tenancyId: r.tenancy?.id || null, month: c.mk }), { disableFuture: false, rowList: consRows, dueDay: (a) => billsDayOf(a, data.settings), action: "לפירוט ולסימון", nav: "cons-history" })} />
      </div></div>

      {consApt && consTenancy && (
        <ConsumptionModal apt={consApt} tenancy={consTenancy} month={consCell.month} data={data} setData={setData} run={run} onClose={() => setConsCell(null)} />
      )}
      {historyApt && historyTenancy && (
        <ConsumptionHistoryPanel apt={historyApt} tenancy={historyTenancy} data={data} onClose={() => setConsHistory(null)} />
      )}
      {editApt && (
        <AmountEditPopup apt={editApt} tenancy={editTenancy} kind={editCell.kind} month={editCell.month}
          initialAmount={editCell.amount} data={data} run={run} onClose={() => setEditCell(null)} />
      )}
    </section>
  );
}
